import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { SearchResult } from '../../../../common/types';

// oxlint-disable-next-line eslint/no-redeclare
const SearchResultMercuryFocusArea = `
... on SearchResultMercuryFocusArea {
	lastModifiedDate
	focusArea {
		id
		uuid
		name
		focusAreaType {
			id
			name
			hierarchyLevel
		}
		status {
			key
			displayName
		}
		health {
			key
			color
			displayName
		}
		targetDate {
			targetDate
			targetDateType
		}
		owner {
			accountId
			name
		}
	}
}
`;

export interface SearchResultMercuryFocusArea extends SearchResult {
	lastModifiedDate?: string;
	focusArea?: {
		id: string;
		uuid: string;
		name: string;
		focusAreaType: {
			id: string;
			name: string;
			hierarchyLevel: number;
		};
		status: {
			key: string;
			displayName: string;
		};
		health?: {
			key: string;
			color: string;
			displayName: string;
		};
		targetDate?: {
			targetDate: string;
			targetDateType: string;
		};
		owner?: {
			accountId: string;
			name: string;
		};
	};
	__typename: 'SearchResultMercuryFocusArea';
}

// oxlint-disable-next-line eslint/no-redeclare
const SearchResultMercuryFocusAreaStatusUpdate = `
... on SearchResultMercuryFocusAreaStatusUpdate {
	lastModifiedDate
	focusArea {
		name
	}
}
`;

export interface SearchResultMercuryFocusAreaStatusUpdate extends SearchResult {
	lastModifiedDate?: string;
	focusArea?: {
		name: string;
	};
	__typename: 'SearchResultMercuryFocusAreaStatusUpdate';
}

// oxlint-disable-next-line eslint/no-redeclare
export interface SearchResultMercuryOrganization extends SearchResult {
	lastModifiedDate?: string;
	organization?: {
		id: string;
		name: string;
		organizationType: {
			id: string;
			name: string;
			hierarchyLevel: number;
		};
		status: {
			key: string;
			displayName: string;
		};
		targetDate?: {
			targetDate: string;
			targetDateType: string;
		};
		owner?: {
			accountId: string;
			name: string;
		};
	};
	__typename: 'SearchResultMercuryOrganization';
}

const SearchResultMercuryOrganization = `
... on SearchResultMercuryOrganization {
	lastModifiedDate
	organization @optIn(to: "Mercury") {
		id
		name
		organizationType {
			id
			name
			hierarchyLevel
		}
		status {
			key
			displayName
		}
		targetDate {
			targetDate
			targetDateType
		}
		owner {
			accountId
			name
		}
	}
}
`;

// oxlint-disable-next-line eslint/no-redeclare
export interface SearchResultMercuryOrganizationStatusUpdate extends SearchResult {
	lastModifiedDate?: string;
	__typename: 'SearchResultMercuryOrganizationStatusUpdate';
}

const SearchResultMercuryOrganizationStatusUpdate = `
... on SearchResultMercuryOrganizationStatusUpdate {
	lastModifiedDate
}
`;

const SearchResultSpfAskPartial = `
... on SearchResultSpfAsk {
	lastModifiedDate
	ask @optIn(to: "Spf") {
		id
		name
		status
		targetDate {
			targetDate
			targetDateType
		}
	}
}
`;

export interface SearchResultSpfAsk extends SearchResult {
	lastModifiedDate?: string;
	ask?: {
		id: string;
		name: string;
		status: string;
		targetDate?: {
			targetDate: string;
			targetDateType: string;
		};
	};
	__typename: 'SearchResultSpfAsk';
}

const SearchResultMercuryRiskPartial = `
... on SearchResultMercuryRisk {
	lastModifiedDate
	risk @optIn(to: "Mercury") {
		id
		name
		status {
			key
			displayName
		}
		targetDate {
			targetDate
			targetDateType
		}
	}
}
`;

// `SearchResultMercuryAsk` exposes a nested `ask` object (hydrated live from Mercury via
// `mercury_asksByIds`), mirroring SPF Ask (`ask @optIn(to: "Spf")`) and Mercury Risk
// (`risk @optIn(to: "Mercury")`). We read status/target date from that object so the data
// is sourced from Mercury directly rather than the search index. The `ask` field is in the
// EXPERIMENTAL lifecycle stage, hence `@optIn(to: "Mercury")`. `id`/`title`/`url` come from
// the shared node selection in `query-generator.tsx`.
const SearchResultMercuryAskPartial = `
... on SearchResultMercuryAsk {
	lastModifiedDate
	mercuryAsk: ask @optIn(to: "Mercury") {
		askStatus
		targetDate {
			targetDate
			targetDateType
		}
	}
}
`;

export interface SearchResultMercuryRisk extends SearchResult {
	lastModifiedDate?: string;
	risk?: {
		id: string;
		name: string;
		status?: {
			key: string;
			displayName: string;
		};
		targetDate?: {
			targetDate: string;
			targetDateType: string;
		};
	};
	__typename: 'SearchResultMercuryRisk';
}

export interface SearchResultMercuryAsk extends SearchResult {
	lastModifiedDate?: string;
	// Nested `ask` object hydrated live from Mercury, aliased to `mercuryAsk` to avoid a
	// relay field collision with SPF Ask's `ask: SpfAsk` — see `SearchResultMercuryAskPartial`.
	mercuryAsk?: {
		askStatus?: string;
		targetDate?: {
			targetDate: string;
			targetDateType: string;
		};
	};
	__typename: 'SearchResultMercuryAsk';
}

export type SearchMercuryPartial =
	| SearchResultMercuryFocusArea
	| SearchResultMercuryFocusAreaStatusUpdate
	| SearchResultSpfAsk
	| SearchResultMercuryAsk
	| SearchResultMercuryRisk
	| SearchResultMercuryOrganization
	| SearchResultMercuryOrganizationStatusUpdate;

export const isSearchResultMercuryFocusAreaPartial = (
	partial: SearchResult,
): partial is SearchResultMercuryFocusArea => {
	return partial?.id?.includes('mercury') && partial.__typename === 'SearchResultMercuryFocusArea';
};

export const isSearchResultMercuryFocusAreaStatusUpdatePartial = (
	partial: SearchResult,
): partial is SearchResultMercuryFocusAreaStatusUpdate => {
	return (
		partial?.id?.includes('mercury') &&
		partial.__typename === 'SearchResultMercuryFocusAreaStatusUpdate'
	);
};

export const isSearchResultMercuryOrganizationPartial = (
	partial: SearchResult,
): partial is SearchResultMercuryOrganization => {
	return (
		partial?.id?.includes('mercury') && partial.__typename === 'SearchResultMercuryOrganization'
	);
};

export const isSearchResultMercuryOrganizationStatusUpdatePartial = (
	partial: SearchResult,
): partial is SearchResultMercuryOrganizationStatusUpdate => {
	return (
		partial?.id?.includes('mercury') &&
		partial.__typename === 'SearchResultMercuryOrganizationStatusUpdate'
	);
};

export const isSearchResultSpfAskPartial = (
	partial: SearchResult,
): partial is SearchResultSpfAsk => {
	return partial?.id?.includes('passionfruit') && partial.__typename === 'SearchResultSpfAsk';
};

export const isSearchResultMercuryRiskPartial = (
	partial: SearchResult,
): partial is SearchResultMercuryRisk => {
	return partial?.id?.includes('mercury') && partial.__typename === 'SearchResultMercuryRisk';
};

export const isSearchResultMercuryAskPartial = (
	partial: SearchResult,
): partial is SearchResultMercuryAsk => {
	return partial?.id?.includes('mercury') && partial.__typename === 'SearchResultMercuryAsk';
};

export const isSearchMercuryPartial = (partial: SearchResult): partial is SearchMercuryPartial => {
	return (
		isSearchResultMercuryFocusAreaPartial(partial) ||
		isSearchResultMercuryFocusAreaStatusUpdatePartial(partial) ||
		isSearchResultSpfAskPartial(partial) ||
		isSearchResultMercuryAskPartial(partial) ||
		isSearchResultMercuryRiskPartial(partial) ||
		isSearchResultMercuryOrganizationPartial(partial) ||
		isSearchResultMercuryOrganizationStatusUpdatePartial(partial)
	);
};

export const getMercuryPartials = () => {
	return `
		${SearchResultMercuryFocusArea}
		${SearchResultMercuryFocusAreaStatusUpdate}
		${
			// The Ask UI migration flag takes precedence over the legacy Passionfruit flag so
			// that only one `ask` selection is ever composed into the query. When both flags are
			// on, requesting both partials would produce two conflicting `ask` fields and the
			// aggregator would reject the operation.
			fg('platform_mercury_ask_search_ui_enabled')
				? SearchResultMercuryAskPartial
				: fg('platform_passionfruit_search_onboarding_enabled')
					? SearchResultSpfAskPartial
					: ''
		}
		${fg('platform_mercury_risk_search_ui_enabled') ? SearchResultMercuryRiskPartial : ''}
		${fg('platform_organization_search_onboarding_enabled') ? SearchResultMercuryOrganization : ''}
		${
			fg('platform_organization_search_onboarding_enabled')
				? SearchResultMercuryOrganizationStatusUpdate
				: ''
		}
	`;
};
